import { Injectable } from '@angular/core';
import { CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { UserAuthService } from '../services/user-auth.service';
import { SolLoginService } from '../services/sol-login.service';


@Injectable({
  providedIn: 'root'
})
export class SolConsentGuard implements CanActivate {

  constructor(
    private readonly router: Router,
    private readonly userAuthService: UserAuthService,
    private readonly solLoginService: SolLoginService
  ) { }

  async canActivate(_: never, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    try {
      await this.userAuthService.oauthReady;
    } catch (err) {
      console.error('[SOL] oauthReady rejected in SolConsentGuard:', err);
    }

    if (await this.hasSession()) {
      console.log('[SOL] Consent guard: session found - no sign-in needed.');
      return true;
    }

    if (!await this.solLoginService.waitForAuthorizeEndpoint()) {
      console.error('[SOL] Discovery document never loaded - falling back to /login.');
      this.solLoginService.useScsSignIn();
      return this.router.parseUrl('/login' + this.solLoginService.campaignParams(this.queryStringOf(state.url)));
    }

    this.solLoginService.startConsentSignIn(this.queryStringOf(state.url));
    return false;
  }

  /**
   * A token in the URL hash means B2C has just handed us one back and AppComponent is still
   * storing it - give that a moment to land instead of firing off a second sign-in for a session
   * we already have.
   */
  private async hasSession(): Promise<boolean> {
    if (this.userAuthService.hasValidSession()) {
      return true;
    }

    const hash = globalThis.location.hash || '';
    if (!hash.includes('access_token') && !hash.includes('id_token')) {
      return false;
    }

    const pollMs = 100;
    for (let waited = 0; waited < 3000; waited += pollMs) {
      await new Promise(resolve => setTimeout(resolve, pollMs));
      if (this.userAuthService.hasValidSession()) {
        return true;
      }
    }
    return false;
  }

  private queryStringOf(url: string): string {
    const queryIndex = url.indexOf('?');
    if (queryIndex < 0) {
      return '';
    }
    return url.substring(queryIndex).split('#')[0];
  }
}
